import { currencyString } from "@qgiv/core-js";

/**
 * @description function that multiplies the subtotal (donation total) by the
 * match ratio
 * @param {object} params
 * @param {number} params.matchRatio
 * @param {number} params.subtotal
 * @returns {number} Amount of $ that will be added to donation by matching sponsor
 */
export const getMatchAmount = ({ matchRatio = 0, subtotal = 0 }) => {
    if (subtotal && matchRatio) {
        return Number(subtotal) * Number(matchRatio);
    }
    return 0;
};

/**
 * @description function to format the impact total with currency
 * @param {object} params
 * @param {object} params.currency
 * @param {number} params.donatedAmount
 * @param {string} params.headerMessageByStatus
 * @param {number} params.matchedAmount
 * @returns {string} Impact total returned as "$100" or an empty string
 */
export const getMatchingCampaignsFormattedImpactTotal = ({
    currency,
    donatedAmount,
    headerMessageByStatus,
    matchedAmount,
}) => {
    const impactTotal = Number(donatedAmount) + Number(matchedAmount);

    const messageContainsImpactAmount =
        headerMessageByStatus.includes("%IMPACTAMOUNT%");

    const matchingCampaignsFormattedImpactTotal = messageContainsImpactAmount
        ? currencyString(impactTotal, currency)
        : "";

    return matchingCampaignsFormattedImpactTotal;
};

/**
 * Formats the match ratio based on the provided values.
 * @param {object} params - The parameters for formatting.
 * @param {object} params.currency
 * @param {number} params.matchRatio
 * @param {number} params.subtotal
 * @returns {string} The formatted match ratio.
 */
export const getFormattedMatchRatio = ({
    currency = {},
    matchRatio = 0,
    subtotal = 0,
}) => {
    const matchAmount = getMatchAmount({ matchRatio, subtotal });

    const halfMatch = subtotal
        ? `+${currencyString(matchAmount, currency)}`
        : "";

    const ratioMap = {
        0.5: halfMatch,
        1: "x1",
        2: "x2",
        3: "x3",
    };

    return ratioMap[matchRatio] || "";
};

/**
 * Calculates the total impact of a donation, including any matching funds.
 * @param {object} params
 * @param {number} params.matchRatio
 * @param {number} params.subtotal
 * @param {object} params.currency
 * @returns {string} The formatted total impact amount.
 */
export const getCalculatedImpact = ({
    matchRatio = 0,
    subtotal = 0,
    currency = {},
}) => {
    const matchAmount = getMatchAmount({ matchRatio, subtotal });
    const impactTotal = Number(subtotal) + Number(matchAmount);
    return impactTotal ? currencyString(impactTotal, currency) : "";
};

/**
 * @description function to format the name into message. If anonymous and %SPONSORNAME% is
 * not in the 1st permission then we need to lowercase that A in "A generous sponsor"
 * @param {object} params
 * @param {boolean} params.anonymousSponsorName
 * @param {string} params.formattedHeaderName
 * @param {string} params.headerMessageByStatus
 * @returns {string} String with formatted message with Sponsor Name
 */
export const getNameFormattedForMessagePosition = ({
    anonymousSponsorName,
    formattedHeaderName,
    headerMessageByStatus,
}) => {
    // Lowercase "A generous sponsor" when in any position but first.
    const shouldLowercaseAnonymousName =
        anonymousSponsorName &&
        headerMessageByStatus.indexOf("%SPONSORNAME%") > 0;

    const nameFormattedForMessagePosition = shouldLowercaseAnonymousName
        ? formattedHeaderName.toLowerCase()
        : formattedHeaderName;

    return nameFormattedForMessagePosition;
};

/**
 * Formats the matching campaign heading based on the provided parameters.
 * @param {object} params
 * @param {boolean} params.anonymousSponsorName
 * @param {boolean} params.hasSelectedRecurringDonation
 * @param {string} params.name
 * @param {string} params.oneTimeMatchingCampaignsTabHeading
 * @param {string} params.recurringMatchingCampaignsTabHeading
 * @returns {string} The formatted matching campaign heading split at the impactTotal for bold styling.
 */
export const getMatchingCampaignFormattedHeading = ({
    anonymousSponsorName = false,
    hasSelectedRecurringDonation = false,
    name = "",
    oneTimeMatchingCampaignsTabHeading = "",
    recurringMatchingCampaignsTabHeading = "",
}) => {
    const headingByFrequency = hasSelectedRecurringDonation
        ? recurringMatchingCampaignsTabHeading
        : oneTimeMatchingCampaignsTabHeading;

    const sponsorName =
        anonymousSponsorName || !name ? "a generous sponsor" : name;

    return headingByFrequency.replace("%SPONSORNAME%", sponsorName);
};

/**
 * @param {object} params
 * @param {object} params.currency
 * @param {string} params.formattedHeaderName
 * @param {boolean} params.recurring
 * @param {object} params.matchingCampaignData
 * @param {object} params.matchingCampaignOptions
 * @returns {object} The formatted matching campaign heading message.
 */
export const getMatchingCampaignHeaderMessage = ({
    currency,
    formattedHeaderName,
    matchingCampaignData = {},
    matchingCampaignOptions = {},
    recurring = false,
}) => {
    const {
        matchedStatus = "2",
        donatedAmount = 0,
        matchedAmount = 0,
        anonymousSponsorName = false,
    } = matchingCampaignData;
    const {
        oneTimeImpactMessage = "%SPONSORNAME% has increased your impact to %IMPACTAMOUNT%!",
        recurringImpactMessage = "%SPONSORNAME% has increased the impact of your first gift to %IMPACTAMOUNT%!",
        matchingCampaignEndedMessage = "Matching from %SPONSORNAME% ended before your gift was submitted, but your impact remains strong!",
    } = matchingCampaignOptions;

    const impactMessage = recurring
        ? recurringImpactMessage
        : oneTimeImpactMessage;

    const headerMessageMap = {
        0: matchingCampaignEndedMessage,
        1: impactMessage,
        2: impactMessage,
    };

    const headerMessageByStatus = headerMessageMap[matchedStatus];

    const matchingCampaignsFormattedImpactTotal =
        getMatchingCampaignsFormattedImpactTotal({
            currency,
            donatedAmount,
            headerMessageByStatus,
            matchedAmount,
        });

    const [
        matchingCampaignHeaderMessageFirst,
        matchingCampaignHeaderMessageLast,
    ] = headerMessageByStatus.split("%IMPACTAMOUNT%");

    const nameFormattedForMessagePosition = getNameFormattedForMessagePosition({
        anonymousSponsorName,
        formattedHeaderName,
        headerMessageByStatus,
    });

    return {
        matchingCampaignHeaderMessageFirst:
            matchingCampaignHeaderMessageFirst?.replace(
                "%SPONSORNAME%",
                nameFormattedForMessagePosition,
            ) || "",
        matchingCampaignsFormattedImpactTotal,
        matchingCampaignHeaderMessageLast:
            matchingCampaignHeaderMessageLast?.replace(
                "%SPONSORNAME%",
                nameFormattedForMessagePosition,
            ) || "",
    };
};

/**
 * @param {object} params
 * @param {object} params.currency
 * @param {string} params.formattedHeaderName
 * @param {object} params.matchingCampaignData
 * @param {object} params.matchingCampaignOptions
 * @returns {string} The formatted matching campaign summary message.
 */
export const getMatchingCampaignSummaryMessage = ({
    currency,
    formattedHeaderName,
    matchingCampaignData = {},
    matchingCampaignOptions = {},
}) => {
    const { matchedStatus = "2", matchedAmount = 0 } = matchingCampaignData;
    const {
        fullMatchingCampaignLabel = "%SPONSORNAME% matched your gift with an extra %MATCHAMOUNT%!",
        partialMatchingCampaignLabel = "%SPONSORNAME% partially matched your gift with %MATCHAMOUNT% before matching funds ran out!",
    } = matchingCampaignOptions;

    const summaryMessageMap = {
        0: "",
        1: partialMatchingCampaignLabel,
        2: fullMatchingCampaignLabel,
    };

    return summaryMessageMap[matchedStatus]
        .replace("%SPONSORNAME%", formattedHeaderName)
        .replace("%MATCHAMOUNT%", currencyString(matchedAmount, currency));
};

/**
 * Builds matching campaign messages based on match type
 * @param {object} params
 * @param {object} params.currency
 * @param {boolean} params.recurring
 * @param {object} params.matchingCampaignData
 * @param {object} params.matchingCampaignOptions
 * @returns {object} The formatted matching campaign heading and summary messages.
 */
export const getReceiptMatchingCampaignMessages = ({
    currency,
    matchingCampaignData = {},
    matchingCampaignOptions = {},
    recurring = false,
}) => {
    const { anonymousSponsorName = false, name = "" } = matchingCampaignData;

    const formattedHeaderName =
        name && !anonymousSponsorName ? name : "A generous sponsor";

    const {
        matchingCampaignHeaderMessageFirst,
        matchingCampaignHeaderMessageLast,
        matchingCampaignsFormattedImpactTotal,
    } = getMatchingCampaignHeaderMessage({
        currency,
        formattedHeaderName,
        matchingCampaignData,
        matchingCampaignOptions,
        recurring,
    });

    const matchingCampaignItemizedMessage = getMatchingCampaignSummaryMessage({
        currency,
        formattedHeaderName,
        matchingCampaignData,
        matchingCampaignOptions,
    });

    return {
        matchingCampaignHeaderMessageFirst,
        matchingCampaignHeaderMessageLast,
        matchingCampaignsFormattedImpactTotal,
        matchingCampaignItemizedMessage,
    };
};
